import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import "./photoAlbums.css";

export default function PhotoAlbums({ username, photos, profile }) {
  const [activeAlbum, setActiveAlbum] = useState(null);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [profilePhotos, setProfilePhotos] = useState([]);
  const [coverPhotos, setCoverPhotos] = useState([]);
  const [timelinePhotos, setTimelinePhotos] = useState([]);

  useEffect(() => {
    if (photos?.resources) {
      // Separate photos by type based on folder path
      const profilePics = photos.resources.filter(
        (img) => img.folder?.includes("profile_pictures") || img.public_id?.includes("profile_pictures")
      );
      const coverPics = photos.resources.filter(
        (img) => img.folder?.includes("cover_pictures") || img.public_id?.includes("cover_pictures")
      );
      const postPics = photos.resources.filter(
        (img) => img.folder?.includes("post_images") || img.public_id?.includes("post_images")
      );

      setProfilePhotos(profilePics);
      setCoverPhotos(coverPics);
      setTimelinePhotos(postPics);
    }
  }, [photos]);

  // Get timeline photos from posts if not available from Cloudinary
  useEffect(() => {
    if (profile?.posts && timelinePhotos.length === 0) {
      const postsWithImages = profile.posts
        .filter((post) => post.images && post.images.length > 0)
        .flatMap((post) =>
          post.images.map((img) => ({
            secure_url: img.url || img,
            public_id: img.public_id || img,
            created_at: post.createdAt,
            postId: post._id,
          }))
        );
      setTimelinePhotos(postsWithImages);
    }
  }, [profile, timelinePhotos.length]);

  const albums = [
    {
      id: "profile",
      name: "Fotos de Perfil",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
          <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
        </svg>
      ),
      photos: profilePhotos,
      cover: profile?.picture,
    },
    {
      id: "cover",
      name: "Fotos de Capa",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
          <path d="M21 3H3C2 3 1 4 1 5v14c0 1.1.9 2 2 2h18c1 0 2-1 2-2V5c0-1-1-2-2-2zM5 17l3.5-4.5 2.5 3.01L14.5 11l4.5 6H5z" />
        </svg>
      ),
      photos: coverPhotos,
      cover: profile?.cover,
    },
    {
      id: "timeline",
      name: "Fotos da Cronologia",
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
          <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zm-5-7l-3 3.72L9 13l-3 4h12l-4-5z" />
        </svg>
      ),
      photos: timelinePhotos,
      cover: timelinePhotos[0]?.secure_url,
    },
  ];

  const openAlbum = (album) => {
    setActiveAlbum(album);
  };

  const closeAlbum = () => {
    setActiveAlbum(null);
    setSelectedPhoto(null);
  };

  const openPhotoViewer = (photo, index) => {
    setSelectedPhoto({ photo, index });
  };

  const closePhotoViewer = () => {
    setSelectedPhoto(null);
  };

  const navigatePhoto = (direction) => {
    if (!selectedPhoto || !activeAlbum) return;
    const photos = activeAlbum.photos;
    let newIndex = selectedPhoto.index + direction;
    if (newIndex < 0) newIndex = photos.length - 1;
    if (newIndex >= photos.length) newIndex = 0;
    setSelectedPhoto({ photo: photos[newIndex], index: newIndex });
  };

  return (
    <div className="photo_albums_container">
      <div className="profile_card">
        <div className="profile_card_header">
          <span>Fotos</span>
          <Link to={`/profile/${username}/photos`} className="profile_header_link">
            Ver todas as fotos
          </Link>
        </div>

        {/* Album Cards Grid */}
        <div className="albums_grid">
          {albums.map((album) => (
            <div
              key={album.id}
              className="album_card"
              onClick={() => openAlbum(album)}
            >
              <div className="album_cover">
                {album.cover ? (
                  <img src={album.cover} alt={album.name} />
                ) : (
                  <div className="album_cover_placeholder">{album.icon}</div>
                )}
                <div className="album_cover_overlay">
                  <span className="album_count">{album.photos.length}</span>
                </div>
              </div>
              <div className="album_info">
                <span className="album_name">{album.name}</span>
                <span className="album_photo_count">
                  {album.photos.length}{" "}
                  {album.photos.length === 1 ? "foto" : "fotos"}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Quick Preview - Recent Photos */}
        {photos?.resources && photos.resources.length > 0 && (
          <>
            <div className="profile_card_count">
              {photos.total_count === 0
                ? ""
                : photos.total_count === 1
                ? "1 Foto"
                : `${photos.total_count} fotos`}
            </div>
            <div className="profile_card_grid">
              {photos.resources.slice(0, 9).map((img, index) => (
                <div
                  className="profile_photo_card"
                  key={img.public_id || index}
                  onClick={() => {
                    setActiveAlbum({ id: "all", name: "Todas as Fotos", photos: photos.resources });
                    openPhotoViewer(img, index);
                  }}
                >
                  <img src={img.secure_url} alt="" />
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Album View Modal */}
      {activeAlbum && !selectedPhoto && (
        <div className="album_modal_overlay" onClick={closeAlbum}>
          <div className="album_modal" onClick={(e) => e.stopPropagation()}>
            <div className="album_modal_header">
              <button className="album_back_btn" onClick={closeAlbum}>
                <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                  <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
                </svg>
              </button>
              <h2>{activeAlbum.name}</h2>
              <span className="album_photo_total">
                {activeAlbum.photos.length} fotos
              </span>
            </div>
            <div className="album_modal_body">
              {activeAlbum.photos.length === 0 ? (
                <div className="album_empty">
                  <div className="album_empty_icon">
                    <svg viewBox="0 0 24 24" fill="currentColor" width="48" height="48">
                      <path d="M21 19V5c0-1.1-.9-2-2-2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2zM8.5 13.5l2.5 3.01L14.5 12l4.5 6H5l3.5-4.5z" />
                    </svg>
                  </div>
                  <p>Nenhuma foto neste álbum</p>
                </div>
              ) : (
                <div className="album_photos_grid">
                  {activeAlbum.photos.map((photo, index) => (
                    <div
                      key={photo.public_id || index}
                      className="album_photo_item"
                      onClick={() => openPhotoViewer(photo, index)}
                    >
                      <img src={photo.secure_url} alt="" />
                      <div className="album_photo_overlay">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                          <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z" />
                        </svg>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Photo Viewer Modal */}
      {selectedPhoto && (
        <div className="photo_viewer_overlay" onClick={closePhotoViewer}>
          <div className="photo_viewer" onClick={(e) => e.stopPropagation()}>
            <button className="photo_viewer_close" onClick={closePhotoViewer}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="24" height="24">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>

            {activeAlbum && activeAlbum.photos.length > 1 && (
              <>
                <button
                  className="photo_viewer_nav photo_viewer_prev"
                  onClick={() => navigatePhoto(-1)}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                    <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                  </svg>
                </button>
                <button
                  className="photo_viewer_nav photo_viewer_next"
                  onClick={() => navigatePhoto(1)}
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="32" height="32">
                    <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                  </svg>
                </button>
              </>
            )}

            <div className="photo_viewer_content">
              <img src={selectedPhoto.photo.secure_url} alt="" />
            </div>

            <div className="photo_viewer_info">
              <span className="photo_viewer_counter">
                {selectedPhoto.index + 1} / {activeAlbum?.photos.length || 1}
              </span>
              {selectedPhoto.photo.created_at && (
                <span className="photo_viewer_date">
                  {new Date(selectedPhoto.photo.created_at).toLocaleDateString("pt-BR", {
                    day: "numeric",
                    month: "long",
                    year: "numeric",
                  })}
                </span>
              )}
            </div>

            <div className="photo_viewer_actions">
              <a
                href={selectedPhoto.photo.secure_url}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="photo_action_btn"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                  <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" />
                </svg>
                <span>Download</span>
              </a>
              {selectedPhoto.photo.postId && (
                <Link
                  to={`/post/${selectedPhoto.photo.postId}`}
                  className="photo_action_btn"
                >
                  <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                    <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                  </svg>
                  <span>Ver publicação</span>
                </Link>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
